import React from 'react';
import { Badge, Drawer, Group, Stack, Table, Text } from '@mantine/core';
import { shellDrawerClassNames } from '../../../app/shellDrawerClassNames';
import type { BaselineRevisionDetail } from '../../../api/baselineApis';
import {
  baselineStatusColor,
  baselineStatusLabel,
  formatBaselineDate,
  formatBaselineTimestamp,
} from '../logic/baselineHistoryUtils';

export interface BaselineRevisionDetailDrawerProps {
  opened: boolean;
  loading: boolean;
  detail: BaselineRevisionDetail | null;
  onClose: () => void;
}

export function BaselineRevisionDetailDrawer({
  opened,
  loading,
  detail,
  onClose,
}: BaselineRevisionDetailDrawerProps) {
  const summary = detail?.summary;
  const title = summary
    ? `${summary.baselineName?.trim() || `Baseline ${summary.baselineIndex}`} · Rev ${summary.revisionNumber}`
    : 'Revision detail';

  return (
    <Drawer
      opened={opened}
      onClose={onClose}
      title={title}
      position="right"
      size="lg"
      padding="md"
      classNames={shellDrawerClassNames}
    >
      {loading ? (
        <Text size="sm" c="dimmed">
          Loading snapshot…
        </Text>
      ) : !detail ? (
        <Text size="sm" c="dimmed">
          No revision detail available.
        </Text>
      ) : (
        <Stack gap="md">
          <Group gap="sm" wrap="wrap">
            <Badge
              variant="light"
              color={baselineStatusColor(detail.summary.status)}
              radius="sm"
              style={{ textTransform: 'none' }}
            >
              {baselineStatusLabel(detail.summary.status)}
            </Badge>
            <Text size="xs" c="dimmed">
              Slot {detail.summary.baselineIndex}
              {detail.summary.taskCount != null ? ` · ${detail.summary.taskCount} tasks` : ''}
            </Text>
          </Group>
          <Text size="xs" c="dimmed">
            Created {formatBaselineTimestamp(detail.summary.createdAt)}
            {detail.summary.createdBy ? ` by ${detail.summary.createdBy}` : ''}
            {detail.summary.reviewedAt
              ? ` · Reviewed ${formatBaselineTimestamp(detail.summary.reviewedAt)}`
              : ''}
          </Text>
          {detail.summary.reviewComment ? (
            <Text size="sm">Review: {detail.summary.reviewComment}</Text>
          ) : null}

          <Table.ScrollContainer minWidth={480}>
            <Table striped highlightOnHover verticalSpacing="xs" horizontalSpacing="sm">
              <Table.Thead>
                <Table.Tr>
                  <Table.Th style={{ width: 180, minWidth: 180, maxWidth: 180 }}>
                    Task
                  </Table.Th>
                  <Table.Th>Start</Table.Th>
                  <Table.Th>Finish</Table.Th>
                  <Table.Th>Duration</Table.Th>
                  <Table.Th>Work</Table.Th>
                  <Table.Th>Cost</Table.Th>
                </Table.Tr>
              </Table.Thead>
              <Table.Tbody>
                {detail.tasks.length === 0 ? (
                  <Table.Tr>
                    <Table.Td colSpan={6}>
                      <Text size="sm" c="dimmed">
                        No task rows in this snapshot.
                      </Text>
                    </Table.Td>
                  </Table.Tr>
                ) : (
                  detail.tasks.map((task) => (
                    <Table.Tr key={task.taskId}>
                      <Table.Td style={{ width: 180, minWidth: 180, maxWidth: 180 }}>
                        <Text
                          size="sm"
                          fw={500}
                          style={{
                            whiteSpace: 'normal',
                            wordBreak: 'break-word',
                            lineHeight: 1.35,
                          }}
                        >
                          {task.taskName?.trim() || task.taskId}
                        </Text>
                      </Table.Td>
                      <Table.Td>
                        <Text size="xs" style={{ fontVariantNumeric: 'tabular-nums' }}>
                          {formatBaselineDate(task.start)}
                        </Text>
                      </Table.Td>
                      <Table.Td>
                        <Text size="xs" style={{ fontVariantNumeric: 'tabular-nums' }}>
                          {formatBaselineDate(task.finish)}
                        </Text>
                      </Table.Td>
                      <Table.Td>
                        <Text size="xs" style={{ fontVariantNumeric: 'tabular-nums' }}>
                          {task.duration != null ? task.duration : '—'}
                        </Text>
                      </Table.Td>
                      <Table.Td>
                        <Text size="xs" style={{ fontVariantNumeric: 'tabular-nums' }}>
                          {task.work != null ? task.work : '—'}
                        </Text>
                      </Table.Td>
                      <Table.Td>
                        <Text size="xs" style={{ fontVariantNumeric: 'tabular-nums' }}>
                          {task.cost != null ? task.cost : '—'}
                        </Text>
                      </Table.Td>
                    </Table.Tr>
                  ))
                )}
              </Table.Tbody>
            </Table>
          </Table.ScrollContainer>
        </Stack>
      )}
    </Drawer>
  );
}
